import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Calculator, FileText, Layers, MessagesSquare, ListChecks, Check, X as XIcon, ArrowRight, Building2, Crown, Quote, ChevronDown, Star, Zap, Brain, Receipt, Shield, PlayCircle, Mic, Sun, Moon } from "lucide-react";
import { Logo, LogoFull, LogoBrand, LogoCompact, Spinner } from "../components/Brand";
import { useApp } from "../AppContext";
import { useTheme } from "../hooks/useTheme";
import ProgereLegalFooter from "../components/ProgereLegalFooter";
import ProgereStoreQR from "../components/ProgereStoreQR";
import VisionDemoInteractive from "../components/VisionDemoInteractive";
import SupportBubble from "../components/SupportBubble";
import ErrorBoundary from "../components/ErrorBoundary";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/66254118-5b42-4f2d-b19d-487f0aca27b5/images/a73149b663b5305331c0c6b9a83255b85fe34b2848fd8aa9cd0632824818deed7.png";

export default function Landing() {
  const { t, lang, setLang, user } = useApp();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const featureIcons = [Sparkles, Calculator, FileText, Layers, MessagesSquare, ListChecks];
  const [openFaq, setOpenFaq] = useState(null);

  // ===== Static content (FR/EN) =====
  const COMPARISON = lang === "fr" ? {
    title: "Pourquoi choisir PROGÈRE Devis AI ?",
    sub: "Comparez-nous aux solutions traditionnelles utilisées au Québec.",
    cols: ["Méthode papier", "Excel / Word", "PROGÈRE Devis AI"],
    rows: [
      ["Temps moyen par soumission", "2-4h", "1-2h", "10-15 minutes"],
      ["Génération assistée par IA", "no", "no", "yes"],
      ["Tarifs CCQ 2026 intégrés", "no", "no", "yes"],
      ["Calcul TPS/TVQ automatique", "no", "partial", "yes"],
      ["Signature numérique du client", "no", "no", "yes"],
      ["Suivi des paiements", "no", "partial", "yes"],
      ["Rapport TPS/TVQ pour comptable", "no", "partial", "yes"],
      ["Conforme Loi 25", "no", "no", "yes"],
      ["Mobile + tablette", "no", "partial", "yes"],
    ],
  } : {
    title: "Why choose PROGÈRE Devis AI?",
    sub: "Compare us to traditional methods used in Quebec.",
    cols: ["Paper", "Excel / Word", "PROGÈRE Devis AI"],
    rows: [
      ["Avg time per quote", "2-4h", "1-2h", "10-15 minutes"],
      ["AI-assisted generation", "no", "no", "yes"],
      ["CCQ 2026 rates built-in", "no", "no", "yes"],
      ["Auto GST/QST calculation", "no", "partial", "yes"],
      ["Client digital signature", "no", "no", "yes"],
      ["Payment tracking", "no", "partial", "yes"],
      ["Tax report for accountant", "no", "partial", "yes"],
      ["Bill 25 compliant", "no", "no", "yes"],
      ["Mobile + tablet", "no", "partial", "yes"],
    ],
  };

  const TESTIMONIALS = lang === "fr" ? [
    { quote: "J'ai gagné 6 heures par semaine sur mes soumissions. L'IA comprend exactement ce que je veux décrire, même à partir de photos prises au chantier.", name: "Marc Tremblay", role: "Entrepreneur général · Lévis", rating: 5 },
    { quote: "Mes clients adorent recevoir le lien de soumission. Ils signent dans la journée au lieu d'attendre des semaines. Mon taux d'acceptation a doublé.", name: "Julie Lavoie", role: "Plomberie résidentielle · Trois-Rivières", rating: 5 },
    { quote: "Le rapport TPS/TVQ exportable en CSV me sauve la vie chaque trimestre. Mon comptable me remercie.", name: "Pierre Gagnon", role: "Rénovation · Montréal", rating: 5 },
  ] : [
    { quote: "I save 6 hours a week on quotes. The AI understands exactly what I want to describe, even from site photos.", name: "Marc Tremblay", role: "General contractor · Lévis", rating: 5 },
    { quote: "My clients love receiving the quote link. They sign within a day instead of waiting weeks. My acceptance rate doubled.", name: "Julie Lavoie", role: "Residential plumbing · Trois-Rivières", rating: 5 },
    { quote: "The GST/QST CSV export saves my life every quarter. My accountant thanks me.", name: "Pierre Gagnon", role: "Renovation · Montréal", rating: 5 },
  ];

  const FAQ = lang === "fr" ? [
    { q: "L'essai gratuit nécessite-t-il une carte de crédit ?", a: "Non. Vous obtenez 3 soumissions gratuites en créant simplement un compte. L'essai Pro de 14 jours s'active aussi sans aucune carte de crédit." },
    { q: "Mes données sont-elles en sécurité au Québec ?", a: "Oui. PROGÈRE Devis AI respecte intégralement la Loi 25 du Québec : consentement clair, Responsable de la protection des renseignements personnels désigné, droits d'accès / suppression / portabilité accessibles en 1 clic. Mots de passe chiffrés avec bcrypt, HTTPS sur toutes les communications." },
    { q: "Comment fonctionne l'IA pour les soumissions ?", a: "Décrivez votre projet en quelques mots, téléversez des photos du chantier, ou uploadez un plan PDF. Notre IA Claude Sonnet 4.5 analyse tout et génère une soumission détaillée avec tarifs CCQ 2026 et taxes québécoises. Vous gardez bien sûr le contrôle final." },
    { q: "Puis-je annuler à tout moment ?", a: "Oui, en un clic depuis votre page Abonnement. L'annulation prend effet à la fin de votre période en cours. Aucun frais de résiliation, aucun engagement." },
    { q: "Est-ce que ça fonctionne sur mobile ?", a: "Oui, l'application est entièrement responsive. Vous pouvez créer des soumissions, prendre des photos du chantier directement avec votre téléphone, et envoyer le devis à un client en sortant du site." },
    { q: "Pourrai-je connecter PROGÈRE Gestion Chantier (ERP) plus tard ?", a: "Absolument. Une couche d'intégration API est déjà disponible (clés API, webhooks sortants sur les événements quote.accepted, invoice.paid, etc.). Votre futur ERP n'aura qu'à se connecter pour synchroniser automatiquement." },
    { q: "Combien coûte le plan Pro ?", a: "249 $ CAD par mois, sans engagement. Jusqu'à 3 utilisateurs, 150 générations IA par mois, IA avancée, templates personnalisés, support prioritaire. Essai 14 jours gratuit pour tester." },
  ] : [
    { q: "Does the free trial require a credit card?", a: "No. You get 3 free quotes by simply creating an account. The 14-day Pro trial also activates without any credit card." },
    { q: "Is my data secure in Quebec?", a: "Yes. PROGÈRE Devis AI fully complies with Quebec's Bill 25: clear consent, designated Privacy Officer, one-click access/deletion/portability rights. Passwords hashed with bcrypt, HTTPS on all communications." },
    { q: "How does the AI work for quotes?", a: "Describe your project in a few words, upload site photos, or upload a PDF plan. Our Claude Sonnet 4.5 AI analyzes everything and generates a detailed quote with 2026 CCQ rates and Quebec taxes. You keep final control." },
    { q: "Can I cancel anytime?", a: "Yes, one click from your Subscription page. Cancellation takes effect at the end of your current period. No cancellation fees, no commitment." },
    { q: "Does it work on mobile?", a: "Yes, the app is fully responsive. You can create quotes, take site photos directly with your phone, and send the quote to a client right after leaving the site." },
    { q: "Can I connect PROGÈRE Construction Management (ERP) later?", a: "Absolutely. An API integration layer is already available (API keys, outbound webhooks on quote.accepted, invoice.paid, etc.). Your future ERP will only need to connect to sync automatically." },
    { q: "How much is the Pro plan?", a: "$249 CAD per month, no commitment. Up to 3 users, 150 AI generations per month, advanced AI, custom templates, priority support. 14-day free trial included." },
  ];

  return (
    <div className="min-h-screen" style={{ background: "transparent" }}>
      {/* NAV */}
      <header className="sticky top-0 z-50 w-full">
        <div className="glass-card mx-3 sm:mx-4 mt-3 sm:mt-4 px-4 sm:px-6 py-3 flex items-center justify-between gap-3" style={{ borderRadius: 999 }}>
          <Link to="/" data-testid="nav-logo"><LogoCompact /></Link>
          <nav className="hidden md:flex items-center gap-7 text-sm" style={{ color: "rgba(245,245,245,0.7)" }}>
            <a href="#features" className="hover:text-[#D4AF37] transition" data-testid="nav-features">{t.nav.features}</a>
            <a href="#pricing" className="hover:text-[#D4AF37] transition" data-testid="nav-pricing">{t.nav.pricing}</a>
            <button onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="text-xs px-3 py-1 rounded-full border border-white/10 hover:border-[#D4AF37]" data-testid="lang-toggle">
              {lang === "fr" ? "EN" : "FR"}
            </button>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="md:hidden text-xs px-2.5 py-1 rounded-full border border-white/10" data-testid="lang-toggle-mobile">
              {lang === "fr" ? "EN" : "FR"}
            </button>
            <button onClick={toggleTheme}
              className="w-9 h-9 rounded-full flex items-center justify-center transition hover:brightness-110"
              style={{ background: "rgba(245,245,245,0.06)", border: "1px solid rgba(245,245,245,0.1)" }}
              aria-label="Toggle theme" data-testid="theme-toggle">
              {theme === "dark" ? <Sun size={14} color="#D4AF37" /> : <Moon size={14} color="#0B0B0B" />}
            </button>
            {user ? (
              <button className="btn-gold text-xs sm:text-sm" onClick={() => nav("/app")} data-testid="nav-app">{t.sidebar.dashboard} <ArrowRight size={14} /></button>
            ) : (
              <>
                <Link to="/login" className="btn-gold text-xs sm:text-sm" data-testid="nav-login">
                  {t.nav.login} <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative hero-grid-bg overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,11,11,0.4) 0%, rgba(11,11,11,0.85) 70%, #0B0B0B 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.2em] mb-10"
                 style={{ background: "rgba(212,175,55,0.08)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.25)" }}>
              <Sparkles size={12} /> {t.hero.badge}
            </div>
            <div className="flex flex-col items-start gap-6">
              <LogoFull size="xl" className="-ml-2 sm:-ml-4" />
            </div>
            <p className="mt-8 max-w-2xl text-lg sm:text-xl" style={{ color: "rgba(245,245,245,0.7)", lineHeight: 1.5 }}>
              {t.hero.subtitle}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button className="btn-red text-base" onClick={() => nav("/register")} data-testid="hero-cta-primary">
                {t.hero.cta1} <ArrowRight size={18} />
              </button>
              <button className="btn-ghost text-base" onClick={() => nav("/login")} data-testid="hero-cta-secondary">
                {t.hero.cta2}
              </button>
            </div>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl">
              {[
                { v: "12 min", l: lang === "fr" ? "soumission moyenne" : "avg quote" },
                { v: "+38%", l: lang === "fr" ? "taux d'approbation" : "approval rate" },
                { v: "TPS/TVQ", l: lang === "fr" ? "calculs automatiques" : "auto-computed" },
                { v: "Claude 4.5", l: lang === "fr" ? "IA intégrée" : "AI inside" },
              ].map((s, i) => (
                <div key={i} className="glass-card p-4 text-center">
                  <div className="font-heading text-2xl font-bold" style={{ color: "#D4AF37" }}>{s.v}</div>
                  <div className="text-xs mt-1" style={{ color: "rgba(245,245,245,0.6)" }}>{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* DEMO VIDEO */}
      <DemoVideoSection lang={lang} />

      {/* FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "#D4AF37" }}>{lang === "fr" ? "Fonctionnalités" : "Features"}</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: "#F5F5F5" }}>{t.features.title}</h2>
          <p className="mt-4 text-lg" style={{ color: "rgba(245,245,245,0.6)" }}>{t.features.sub}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.features.list.map((f, i) => {
            const Icon = featureIcons[i] || Sparkles;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="glass-card p-7">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-5" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.25)" }}>
                  <Icon size={20} color="#D4AF37" />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2" style={{ color: "#F5F5F5" }}>{f.t}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "rgba(245,245,245,0.6)" }}>{f.d}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 sm:py-28">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "#D4AF37" }}>
            {lang === "fr" ? "Comparaison" : "Comparison"}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: "#F5F5F5" }}>{COMPARISON.title}</h2>
          <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: "rgba(245,245,245,0.6)" }}>{COMPARISON.sub}</p>
        </div>
        <div className="glass-card p-2 sm:p-4 overflow-x-auto" data-testid="comparison-table">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr>
                <th></th>
                {COMPARISON.cols.map((c, i) => (
                  <th key={c} className="text-center px-3 py-4">
                    <span className="text-xs uppercase tracking-wider" style={{
                      color: i === 2 ? "#D4AF37" : "rgba(245,245,245,0.5)",
                      fontWeight: i === 2 ? 700 : 400,
                    }}>{c}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.rows.map((row, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="px-3 py-4 text-sm" style={{ color: "rgba(245,245,245,0.85)" }}>{row[0]}</td>
                  {row.slice(1).map((cell, j) => (
                    <td key={j} className="text-center px-3 py-4"
                      style={{ background: j === 2 ? "rgba(212,175,55,0.04)" : "transparent" }}>
                      {cell === "yes" ? <Check size={18} color="#4ADE80" className="mx-auto" />
                        : cell === "no" ? <XIcon size={18} color="rgba(245,245,245,0.3)" className="mx-auto" />
                        : cell === "partial" ? <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37" }}>~</span>
                        : <span className="text-sm" style={{ color: j === 2 ? "#D4AF37" : "rgba(245,245,245,0.7)", fontWeight: j === 2 ? 700 : 400 }}>{cell}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 sm:py-28">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "#D4AF37" }}>
            {lang === "fr" ? "Témoignages" : "Testimonials"}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: "#F5F5F5" }}>
            {lang === "fr" ? "Les entrepreneurs québécois nous font confiance" : "Quebec contractors trust us"}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="testimonials">
          {TESTIMONIALS.map((tm, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="glass-card p-7 relative" data-testid={`testimonial-${i}`}>
              <Quote size={28} color="#D4AF37" className="opacity-30 mb-3" />
              <div className="flex gap-1 mb-3">
                {[...Array(tm.rating)].map((_, j) => <Star key={j} size={14} color="#D4AF37" fill="#D4AF37" />)}
              </div>
              <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(245,245,245,0.85)" }}>« {tm.quote} »</p>
              <div className="pt-4 border-t border-white/5">
                <div className="font-heading text-sm font-bold" style={{ color: "#F5F5F5" }}>{tm.name}</div>
                <div className="text-xs" style={{ color: "rgba(245,245,245,0.55)" }}>{tm.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SECONDARY CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="glass-strong p-8 sm:p-12 relative overflow-hidden text-center" data-testid="secondary-cta">
          <div className="absolute top-0 right-0 w-80 h-80 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)", transform: "translate(30%, -30%)" }} />
          <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-15" style={{ background: "radial-gradient(circle, #C00000 0%, transparent 70%)", transform: "translate(-30%, 30%)" }} />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-widest mb-4"
              style={{ background: "rgba(74,222,128,0.12)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }}>
              <Zap size={12} /> {lang === "fr" ? "3 soumissions gratuites · Aucune carte" : "3 free quotes · No card"}
            </div>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold mb-4" style={{ color: "#F5F5F5" }}>
              {lang === "fr" ? "Économisez " : "Save "}
              <span style={{ color: "#D4AF37" }}>{lang === "fr" ? "6 heures par semaine" : "6 hours per week"}</span>
              {lang === "fr" ? " sur vos soumissions." : " on your quotes."}
            </h2>
            <p className="text-base max-w-2xl mx-auto mb-8" style={{ color: "rgba(245,245,245,0.7)" }}>
              {lang === "fr"
                ? "Inscrivez-vous gratuitement. Testez l'IA sur 3 vraies soumissions. Activez l'essai Pro 14 jours quand vous êtes prêt."
                : "Sign up free. Test the AI on 3 real quotes. Activate the 14-day Pro trial whenever you're ready."}
            </p>
            <button onClick={() => nav("/register")} className="btn-red text-base px-8 py-3.5" data-testid="secondary-cta-btn">
              {lang === "fr" ? "Créer mon compte gratuit" : "Create my free account"} <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <PricingSection lang={lang} user={user} nav={nav} />

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-20 sm:py-28">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "#D4AF37" }}>FAQ</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: "#F5F5F5" }}>
            {lang === "fr" ? "Questions fréquentes" : "Frequently asked questions"}
          </h2>
        </div>
        <div className="space-y-3" data-testid="faq-section">
          {FAQ.map((item, i) => (
            <div key={i} className="glass-card overflow-hidden" data-testid={`faq-${i}`}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-white/2 transition"
                data-testid={`faq-toggle-${i}`}>
                <span className="font-heading text-base font-semibold" style={{ color: "#F5F5F5" }}>{item.q}</span>
                <ChevronDown size={18} color="#D4AF37" style={{ transform: openFaq === i ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }} />
              </button>
              {openFaq === i && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0 }}
                  className="px-5 pb-5">
                  <p className="text-sm leading-relaxed" style={{ color: "rgba(245,245,245,0.78)" }}>{item.a}</p>
                </motion.div>
              )}
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <p className="text-sm mb-4" style={{ color: "rgba(245,245,245,0.6)" }}>
            {lang === "fr" ? "Une autre question ?" : "Another question?"}
          </p>
          <a href="mailto:support@xn--progre-6ua.com" className="btn-ghost text-sm" data-testid="faq-contact">
            <MessagesSquare size={14} /> {lang === "fr" ? "Nous écrire" : "Contact us"}
          </a>
        </div>
      </section>

      {/* PROGÈRE VISION — Démo interactive */}
      <VisionDemoInteractive />

      {/* PROGÈRE MOBILE — QR codes */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
        <ProgereStoreQR variant="full" />
      </section>

      <ProgereLegalFooter />
      <ErrorBoundary><SupportBubble /></ErrorBoundary>
    </div>
  );
}

// ============ DEMO VIDEO SECTION ============
// Replace DEMO_VIDEO_URL with your Loom / YouTube unlisted URL when ready.
// e.g. "https://www.loom.com/embed/xxxx" or "https://www.youtube.com/embed/xxxx".
const DEMO_VIDEO_URL = "";

// ============ PRICING SECTION ============
// 4 cards style PROGÈRE PUNCH: Starter · Pro (Popular) · Enterprise · Écosystème PROGÈRE 360
const PRICING_PLANS = [
  {
    id: "starter", name: "Starter", priceMonthly: 99, priceAnnual: 89,
    tagFr: "1 utilisateur · 25 générations IA/mois", tagEn: "1 user · 25 AI generations/mo",
    featuresFr: [
      "Création de devis assistée par IA",
      "Modèles de devis professionnels",
      "Gestion des clients",
      "Historique des devis",
      "Exportation PDF",
      "Bibliothèque de services et matériaux",
      "1 utilisateur",
      "Jusqu'à 25 générations IA par mois",
    ],
    featuresEn: [
      "AI-assisted quote creation", "Professional quote templates", "Client management",
      "Quote history", "PDF export", "Services and materials library",
      "1 user", "Up to 25 AI generations per month",
    ],
  },
  {
    id: "pro", name: "Pro", priceMonthly: 249, priceAnnual: 224, featured: true,
    tagFr: "Jusqu'à 3 utilisateurs · 150 générations IA/mois", tagEn: "Up to 3 users · 150 AI generations/mo",
    featuresFr: [
      "Toutes les fonctions Starter",
      "Création avancée de devis avec IA",
      "Bibliothèque avancée de prix",
      "Gestion avancée des clients",
      "Relances et suivis",
      "Modèles personnalisés",
      "Rapports de performance",
      "Jusqu'à 3 utilisateurs",
      "Jusqu'à 150 générations IA par mois",
    ],
    featuresEn: [
      "All Starter features", "Advanced AI quote creation", "Advanced price library",
      "Advanced client management", "Follow-ups and reminders", "Custom templates",
      "Performance reports", "Up to 3 users", "Up to 150 AI generations per month",
    ],
  },
  {
    id: "enterprise", name: "Entreprise", priceMonthly: 499, priceAnnual: 449,
    tagFr: "Jusqu'à 10 utilisateurs · 500 générations IA/mois", tagEn: "Up to 10 users · 500 AI generations/mo",
    featuresFr: [
      "Toutes les fonctions Pro",
      "Automatisations avancées",
      "Gestion multiutilisateur",
      "Permissions et rôles",
      "Rapports avancés",
      "Gestion de plusieurs équipes",
      "Support prioritaire",
      "Jusqu'à 10 utilisateurs",
      "Jusqu'à 500 générations IA par mois",
    ],
    featuresEn: [
      "All Pro features", "Advanced automations", "Multi-user management",
      "Permissions and roles", "Advanced reports", "Multi-team management",
      "Priority support", "Up to 10 users", "Up to 500 AI generations per month",
    ],
  },
];

function PricingSection({ lang, user, nav }) {
  const [billing, setBilling] = useState("monthly");
  const isFr = lang === "fr";
  const isAnnual = billing === "annual";
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 sm:py-28">
      <div className="text-center mb-10">
        <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "#D4AF37" }}>
          {isFr ? "Tarifs" : "Pricing"}
        </div>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: "#F5F5F5" }}>
          {isFr ? "Des forfaits pour chaque entreprise" : "Plans for every business"}
        </h2>
        <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: "rgba(245,245,245,0.6)" }}>
          {isFr ? "Sans engagement. Évoluez à mesure que votre équipe grandit." : "No commitment. Scale as your team grows."}
        </p>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-12" data-testid="billing-toggle">
        <button onClick={() => setBilling("monthly")}
          className="px-5 py-2 rounded-full text-sm font-semibold transition"
          style={{
            background: !isAnnual ? "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)" : "transparent",
            color: !isAnnual ? "#0B0B0B" : "rgba(245,245,245,0.75)",
            border: !isAnnual ? "1px solid rgba(212,175,55,0.5)" : "1px solid rgba(245,245,245,0.1)",
          }}
          data-testid="billing-monthly">
          {isFr ? "Mensuel" : "Monthly"}
        </button>
        <button onClick={() => setBilling("annual")}
          className="px-5 py-2 rounded-full text-sm font-semibold transition flex items-center gap-2"
          style={{
            background: isAnnual ? "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)" : "transparent",
            color: isAnnual ? "#0B0B0B" : "rgba(245,245,245,0.75)",
            border: isAnnual ? "1px solid rgba(212,175,55,0.5)" : "1px solid rgba(245,245,245,0.1)",
          }}
          data-testid="billing-annual">
          {isFr ? "Annuel" : "Annual"}
          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
            style={{ background: "rgba(74,222,128,0.2)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.4)" }}>
            {isFr ? "Économisez 10%" : "Save 10%"}
          </span>
        </button>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {PRICING_PLANS.map((p) => {
          const price = isAnnual ? p.priceAnnual : p.priceMonthly;
          const features = isFr ? p.featuresFr : p.featuresEn;
          return (
            <div key={p.id} className="relative rounded-3xl p-6 sm:p-7 flex flex-col"
              style={{
                background: p.featured
                  ? "linear-gradient(180deg, rgba(24,24,24,0.95), rgba(11,11,11,0.98))"
                  : "linear-gradient(180deg, rgba(18,18,18,0.85), rgba(11,11,11,0.9))",
                border: p.featured ? "1px solid rgba(212,175,55,0.5)" : "1px solid rgba(245,245,245,0.08)",
                boxShadow: p.featured ? "0 20px 60px -20px rgba(212,175,55,0.4)" : "0 10px 30px -10px rgba(0,0,0,0.5)",
              }}
              data-testid={`pricing-card-${p.id}`}>
              {p.featured && (
                <div className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
                  style={{ background: "rgba(74,222,128,0.15)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.4)" }}>
                  {isFr ? "Actif" : "Active"}
                </div>
              )}

              <div className="flex items-center gap-2 mb-2">
                <Zap size={14} color="#D4AF37" />
                <span className="text-xs" style={{ color: "rgba(245,245,245,0.7)" }}>PROGÈRE DEVIS AI</span>
              </div>
              <h3 className="font-heading text-lg font-bold mb-4" style={{ color: "#F5F5F5" }}>{p.name}</h3>

              <div className="flex items-baseline gap-1 mb-2">
                <span className="font-heading text-4xl font-black" style={{ color: "#F5F5F5" }}>{price}</span>
                <span className="text-sm font-semibold" style={{ color: "rgba(245,245,245,0.6)" }}>CAD</span>
                <span className="text-xs ml-1" style={{ color: "rgba(245,245,245,0.5)" }}>/{isFr ? "mois" : "mo"}</span>
              </div>
              {isAnnual && (
                <div className="text-[11px] mb-4" style={{ color: "#4ADE80" }}>
                  {isFr ? `Facturé ${price * 12} CAD/an` : `Billed ${price * 12} CAD/yr`}
                </div>
              )}

              <div className="rounded-xl px-3 py-2.5 mb-5 text-xs" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(245,245,245,0.06)", color: "rgba(245,245,245,0.85)" }}>
                {isFr ? p.tagFr : p.tagEn}
              </div>

              <ul className="space-y-2.5 mb-6 text-sm flex-1">
                {features.map((f) => (
                  <li key={f} className="flex gap-2 items-start" style={{ color: "rgba(245,245,245,0.8)" }}>
                    <Check size={14} color="#4ADE80" className="mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button className="btn-gold w-full text-sm mt-auto"
                onClick={() => {
                  if (user) return nav(`/app/billing?plan=${p.id}&module=devis_ai&billing=${billing}`);
                  return nav(`/register?plan=${p.id}&module=devis_ai&billing=${billing}`);
                }}
                data-testid={`pricing-cta-${p.id}`}>
                {isFr ? "Choisir ce forfait" : "Choose this plan"}
              </button>
            </div>
          );
        })}

        {/* PROGÈRE 360 — Écosystème complet */}
        <div className="relative rounded-3xl p-6 sm:p-7 flex flex-col md:col-span-2 xl:col-span-1"
          style={{
            background: "linear-gradient(180deg, rgba(24,24,24,0.95), rgba(11,11,11,0.98))",
            border: "1px solid rgba(212,175,55,0.4)",
            boxShadow: "0 20px 60px -20px rgba(212,175,55,0.35)",
          }}
          data-testid="pricing-card-360">
          <div className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
            style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" }}>
            <Crown size={9} /> {isFr ? "Écosystème" : "Ecosystem"}
          </div>

          <div className="flex items-center gap-2 mb-2">
            <Crown size={14} color="#D4AF37" />
            <span className="text-xs font-bold" style={{ color: "#D4AF37" }}>
              PROGÈRE 360 — {isFr ? "Écosystème complet" : "Full ecosystem"}
            </span>
          </div>

          <div className="font-heading text-2xl font-black mb-1" style={{ color: "#F5F5F5" }}>
            {isFr ? "Sur demande" : "Custom"}
          </div>

          <div className="mt-3 mb-4 space-y-1.5 text-sm" style={{ color: "rgba(245,245,245,0.8)" }}>
            <div className="flex items-center gap-2"><Building2 size={12} color="#D4AF37" /> {isFr ? "Illimités utilisateurs" : "Unlimited users"}</div>
            <div className="flex items-center gap-2"><Building2 size={12} color="#D4AF37" /> {isFr ? "Illimités employés" : "Unlimited employees"}</div>
          </div>

          <div className="rounded-xl p-3 mb-5" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.2)" }}>
            <div className="text-[10px] uppercase tracking-widest mb-2 flex items-center gap-1" style={{ color: "#D4AF37" }}>
              <Crown size={9} /> {isFr ? "Suite complète incluse" : "Full suite included"}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {["PROGÈRE ERP", "PROGÈRE COMPTA", "PROGÈRE DEVIS AI", "PROGÈRE PUNCH"].map((m) => (
                <span key={m} className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider"
                  style={{ background: "rgba(212,175,55,0.12)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.3)" }}>
                  {m}
                </span>
              ))}
            </div>
          </div>

          <ul className="space-y-2 mb-6 text-sm flex-1">
            {(isFr ? [
              "Tout Entreprise",
              "Utilisateurs & employés illimités",
              "ERP unifié & SSO",
              "Gestionnaire de compte dédié",
              "Déploiement & formation",
            ] : [
              "All Enterprise",
              "Unlimited users & employees",
              "Unified ERP & SSO",
              "Dedicated account manager",
              "Deployment & training",
            ]).map((f) => (
              <li key={f} className="flex gap-2 items-start" style={{ color: "rgba(245,245,245,0.8)" }}>
                <Check size={14} color="#4ADE80" className="mt-0.5 flex-shrink-0" /> <span>{f}</span>
              </li>
            ))}
          </ul>

          <button className="btn-gold w-full text-sm" onClick={() => nav("/erp-demo")} data-testid="pricing-cta-360">
            <Crown size={13} /> {isFr ? "Demander une soumission" : "Request a quote"}
          </button>
        </div>
      </div>

      {/* Multi-module discount hint */}
      <div className="mt-8 text-center text-xs" style={{ color: "rgba(245,245,245,0.55)" }}>
        {isFr
          ? "💡 Rabais automatique multi-modules : 10 % dès 2 modules · 15 % dès 3 modules · 20 % dès 4 modules"
          : "💡 Automatic multi-module discount: 10% from 2 modules · 15% from 3 modules · 20% from 4 modules"}
      </div>

      <p className="mt-4 text-center text-[11px] max-w-2xl mx-auto" style={{ color: "rgba(245,245,245,0.4)" }}>
        {isFr
          ? "Les montants affichés sont en dollars canadiens. Les taxes applicables peuvent s'ajouter selon la province ou le pays. Les devis générés par PROGÈRE DEVIS AI doivent être validés par l'entreprise avant d'être transmis officiellement à un client."
          : "Amounts shown are in Canadian dollars. Applicable taxes may apply based on province or country. Quotes generated by PROGÈRE DEVIS AI must be validated by the company before being officially transmitted to a client."}
      </p>
    </section>
  );
}

// ============ INTERACTIVE LIVE DEMO ============

function DemoVideoSection({ lang }) {
  const isFr = lang === "fr";
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState("");

  const PROMPT_FR = "Nettoyage post-construction · Tour à condos · 32 000 pi² · 3 étages · livraison lundi";
  const PROMPT_EN = "Post-construction cleaning · Condo tower · 32,000 sqft · 3 floors · delivery Monday";
  const PROMPT = isFr ? PROMPT_FR : PROMPT_EN;

  const ITEMS_FR = [
    { d: "Aspiration industrielle HEPA · 32 000 pi²", q: 32000, u: "pi²", t: 4800 },
    { d: "Nettoyage vitres intérieures + châssis", q: 180, u: "u", t: 2160 },
    { d: "Lavage plancher céramique · marches", q: 32000, u: "pi²", t: 3520 },
    { d: "Détail sanitaires · robinetterie chromée", q: 24, u: "u", t: 960 },
    { d: "Enlèvement et disposition débris", q: 1, u: "lot", t: 1200 },
  ];
  const ITEMS_EN = [
    { d: "Industrial HEPA vacuuming · 32,000 sqft", q: 32000, u: "sqft", t: 4800 },
    { d: "Interior windows + frames cleaning", q: 180, u: "u", t: 2160 },
    { d: "Ceramic floor + stairs wash", q: 32000, u: "sqft", t: 3520 },
    { d: "Sanitary detailing · chrome fixtures", q: 24, u: "u", t: 960 },
    { d: "Debris removal & disposal", q: 1, u: "lot", t: 1200 },
  ];
  const ITEMS = isFr ? ITEMS_FR : ITEMS_EN;

  // Pause the entire interactive demo when the tab is hidden (mobile Safari
  // aggressively kills tabs with active animations after ~60s of background
  // memory pressure — pausing releases Framer Motion animation frames).
  const [paused, setPaused] = useState(false);
  // Auto-stop the infinite demo loop after N cycles to prevent memory bloat.
  // User can click "Rejouer" to restart the demo. Prevents crashes on iOS Safari
  // after ~2 min of continuous background animation.
  const [cycles, setCycles] = useState(0);
  const [demoStopped, setDemoStopped] = useState(false);
  const MAX_CYCLES = 3;

  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Typewriter effect for prompt
  useEffect(() => {
    if (paused || demoStopped) return;
    if (step !== 0) return;
    let i = 0;
    setTyped("");
    const id = setInterval(() => {
      i++;
      setTyped(PROMPT.slice(0, i));
      if (i >= PROMPT.length) {
        clearInterval(id);
        setTimeout(() => setStep(1), 500);
      }
    }, 32);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [step, lang, paused, demoStopped]);

  // AI thinking → items reveal → PDF export → loop (max MAX_CYCLES times)
  useEffect(() => {
    if (paused || demoStopped) return;
    if (step === 1) {
      const t = setTimeout(() => setStep(2), 1200);
      return () => clearTimeout(t);
    }
    if (step === 2) {
      const t = setTimeout(() => setStep(3), 3200);
      return () => clearTimeout(t);
    }
    if (step === 3) {
      const t = setTimeout(() => setStep(4), 2200);
      return () => clearTimeout(t);
    }
    if (step === 4) {
      const t = setTimeout(() => {
        setCycles((c) => {
          const next = c + 1;
          if (next >= MAX_CYCLES) {
            setDemoStopped(true);  // Stop loop — user can click Replay
          } else {
            setStep(0);
          }
          return next;
        });
      }, 3800);
      return () => clearTimeout(t);
    }
  }, [step, paused, demoStopped]);

  const replayDemo = () => {
    setCycles(0);
    setDemoStopped(false);
    setStep(0);
  };

  const subtotal = ITEMS.reduce((s, it) => s + it.t, 0);
  const gst = subtotal * 0.05;
  const qst = subtotal * 0.09975;
  const total = subtotal + gst + qst;

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 sm:py-28" data-testid="demo-video-section">
      <div className="text-center mb-10">
        <div className="text-xs uppercase tracking-[0.3em] mb-3 inline-flex items-center gap-2" style={{ color: "#D4AF37" }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#4ADE80", boxShadow: "0 0 8px #4ADE80" }} />
          {isFr ? "Démo en direct" : "Live demo"}
        </div>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: "#F5F5F5" }}>
          {isFr ? "Une soumission complète en 30 secondes" : "A full quote in 30 seconds"}
        </h2>
        <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: "rgba(245,245,245,0.6)" }}>
          {isFr
            ? "Décrivez le projet · l'IA rédige la soumission · vous exportez le PDF. Regardez en temps réel."
            : "Describe the project · AI writes the quote · you export the PDF. Watch it happen live."}
        </p>
        {demoStopped && (
          <button onClick={replayDemo} data-testid="demo-replay-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition active:scale-95"
            style={{ background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)", color: "#0B0C0F", boxShadow: "0 4px 12px rgba(212,175,55,0.35)" }}>
            <PlayCircle size={16} strokeWidth={2.5} />
            {isFr ? "Rejouer la démo" : "Replay demo"}
          </button>
        )}
      </div>

      {/* Live app mockup */}
      <div className="relative max-w-5xl mx-auto rounded-3xl overflow-hidden"
        style={{
          background: "radial-gradient(circle at 20% 0%, rgba(212,175,55,0.15), transparent 55%), radial-gradient(circle at 80% 100%, rgba(192,0,0,0.12), transparent 55%), linear-gradient(180deg, rgba(24,24,24,0.98), rgba(11,11,11,0.98))",
          border: "1px solid rgba(212,175,55,0.25)",
          boxShadow: "0 40px 80px -20px rgba(0,0,0,0.8), 0 20px 60px -20px rgba(212,175,55,0.35)",
        }}>
        {/* Fake window bar */}
        <div className="flex items-center gap-2 px-5 py-3 border-b" style={{ borderColor: "rgba(212,175,55,0.12)", background: "rgba(11,11,11,0.5)" }}>
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ background: "#FF5F57" }} />
            <div className="w-3 h-3 rounded-full" style={{ background: "#FEBC2E" }} />
            <div className="w-3 h-3 rounded-full" style={{ background: "#28C840" }} />
          </div>
          <div className="ml-4 text-[11px]" style={{ color: "rgba(245,245,245,0.5)" }}>
            devis.progere.ca / nouvelle soumission
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "rgba(74,222,128,0.15)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }}>● {isFr ? "En direct" : "Live"}</span>
          </div>
        </div>

        <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: prompt + AI */}
          <div className="lg:col-span-3 space-y-4">
            <div className="rounded-2xl p-5" style={{ background: "rgba(11,11,11,0.7)", border: "1px solid rgba(212,175,55,0.2)" }}>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={16} color="#D4AF37" />
                <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#D4AF37" }}>
                  {isFr ? "Décrivez le projet" : "Describe the project"}
                </span>
                <span className="ml-auto flex items-center gap-1 text-[10px]" style={{ color: "rgba(245,245,245,0.5)" }}>
                  <Mic size={11} color="#C00000" /> {isFr ? "Micro actif" : "Mic on"}
                </span>
              </div>
              <div className="text-sm min-h-[3rem] font-mono" style={{ color: "#F5F5F5", lineHeight: 1.6 }}>
                {typed}
                <span className="inline-block w-1.5 h-4 ml-0.5 align-middle animate-pulse" style={{ background: "#D4AF37" }} />
              </div>
            </div>

            {step >= 1 && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-5 relative overflow-hidden"
                style={{ background: "linear-gradient(90deg, rgba(212,175,55,0.08), rgba(192,0,0,0.06))", border: "1px solid rgba(212,175,55,0.3)" }}>
                {step === 1 && (
                  <div className="absolute inset-0 shimmer" />
                )}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.2)", border: "1px solid rgba(212,175,55,0.4)" }}>
                    <Sparkles size={11} color="#D4AF37" />
                  </div>
                  <span className="text-[11px] uppercase tracking-widest font-bold" style={{ color: "#D4AF37" }}>
                    {step === 1
                      ? (isFr ? "Claude Sonnet 4.5 analyse..." : "Claude Sonnet 4.5 analyzing...")
                      : (isFr ? "Soumission générée par IA" : "AI-generated quote")}
                  </span>
                </div>
                <div className="space-y-2">
                  {step === 1 && (
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <motion.div key={i}
                          animate={{ opacity: [0.2, 1, 0.2] }}
                          transition={{ duration: 1.2, repeat: 2, delay: i * 0.2 }}
                          className="w-2 h-2 rounded-full" style={{ background: "#D4AF37" }} />
                      ))}
                    </div>
                  )}
                  {step >= 2 && ITEMS.map((it, i) => (
                    <motion.div key={i}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.35, delay: step === 2 ? i * 0.4 : 0 }}
                      className="flex items-center justify-between gap-3 py-1.5 px-3 rounded-lg text-xs"
                      style={{ background: "rgba(11,11,11,0.5)", border: "1px solid rgba(245,245,245,0.05)" }}>
                      <div className="flex-1 min-w-0 truncate" style={{ color: "#F5F5F5" }}>
                        <span style={{ color: "rgba(212,175,55,0.7)" }}>{i + 1}.</span> {it.d}
                      </div>
                      <div className="text-[10px] flex-shrink-0" style={{ color: "rgba(245,245,245,0.55)" }}>
                        {it.q.toLocaleString(isFr ? "fr-CA" : "en-CA")} {it.u}
                      </div>
                      <div className="font-mono font-bold flex-shrink-0" style={{ color: "#D4AF37" }}>
                        {isFr ? `${it.t.toLocaleString("fr-CA")} $` : `$${it.t.toLocaleString("en-CA")}`}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* Right: totals + status */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl p-5" style={{ background: "rgba(11,11,11,0.7)", border: "1px solid rgba(245,245,245,0.08)" }}>
              <div className="text-[10px] uppercase tracking-widest font-bold mb-4" style={{ color: "rgba(245,245,245,0.5)" }}>
                {isFr ? "Récapitulatif" : "Summary"}
              </div>
              {[
                { label: isFr ? "Sous-total" : "Subtotal", value: subtotal, show: step >= 2 },
                { label: "TPS 5%", value: gst, show: step >= 2 },
                { label: "TVQ 9.975%", value: qst, show: step >= 2 },
              ].map((row, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: row.show ? 1 : 0.15 }}
                  className="flex justify-between text-xs py-2 border-b" style={{ borderColor: "rgba(245,245,245,0.05)", color: "rgba(245,245,245,0.85)" }}>
                  <span>{row.label}</span>
                  <span className="font-mono">{isFr ? `${row.value.toFixed(2).replace(".", ",")} $` : `$${row.value.toFixed(2)}`}</span>
                </motion.div>
              ))}
              <motion.div
                animate={{ opacity: step >= 2 ? 1 : 0.3, scale: step === 3 || step === 4 ? [1, 1.03, 1] : 1 }}
                transition={{ duration: 0.6 }}
                className="flex justify-between items-baseline pt-3">
                <span className="text-xs uppercase tracking-widest font-bold" style={{ color: "#D4AF37" }}>Total</span>
                <span className="font-heading text-2xl font-black" style={{ color: "#F5F5F5" }}>
                  {isFr ? `${total.toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $` : `$${total.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </span>
              </motion.div>
            </div>

            {/* PDF export button */}
            <motion.div
              animate={{
                background: step === 3 ? "linear-gradient(180deg, #C00000, #A00000)" : (step >= 4 ? "linear-gradient(180deg, rgba(74,222,128,0.2), rgba(74,222,128,0.1))" : "rgba(11,11,11,0.7)"),
                borderColor: step === 3 ? "#C00000" : (step >= 4 ? "rgba(74,222,128,0.4)" : "rgba(245,245,245,0.08)"),
              }}
              className="rounded-2xl p-4 flex items-center justify-between border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: step >= 4 ? "rgba(74,222,128,0.2)" : (step === 3 ? "rgba(255,255,255,0.15)" : "rgba(212,175,55,0.1)") }}>
                  {step >= 4 ? <Check size={18} color="#4ADE80" /> : <FileText size={18} color={step === 3 ? "#F5F5F5" : "#D4AF37"} />}
                </div>
                <div>
                  <div className="text-sm font-bold" style={{ color: "#F5F5F5" }}>
                    {step >= 4
                      ? (isFr ? "PDF exporté & envoyé" : "PDF exported & sent")
                      : step === 3
                        ? (isFr ? "Génération PDF..." : "Generating PDF...")
                        : (isFr ? "Exporter en PDF" : "Export as PDF")}
                  </div>
                  <div className="text-[11px]" style={{ color: "rgba(245,245,245,0.55)" }}>
                    {step >= 4 ? (isFr ? "Client notifié · signature en attente" : "Client notified · awaiting signature") : (isFr ? "Modèle PROGÈRE · TPS/TVQ" : "PROGÈRE template · GST/QST")}
                  </div>
                </div>
              </div>
              {step === 3 && <Spinner />}
            </motion.div>

            {/* Progress steps */}
            <div className="flex items-center gap-1.5 justify-center pt-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-1 rounded-full transition-all"
                  style={{
                    width: step === i ? 24 : 8,
                    background: step >= i ? "#D4AF37" : "rgba(245,245,245,0.1)",
                    boxShadow: step === i ? "0 0 6px rgba(212,175,55,0.6)" : "none",
                  }} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Feature strip below */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl mx-auto">
        {[
          { icon: Mic, label: isFr ? "Dictée vocale" : "Voice dictation", color: "#C00000" },
          { icon: Sparkles, label: isFr ? "IA Claude 4.5" : "Claude 4.5 AI", color: "#D4AF37" },
          { icon: Receipt, label: "TPS · TVQ", color: "#4ADE80" },
          { icon: Zap, label: isFr ? "Sync ERP" : "ERP sync", color: "#F5D062" },
        ].map((f, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-2.5 rounded-full"
            style={{ background: "rgba(11,11,11,0.6)", border: "1px solid rgba(245,245,245,0.06)" }}>
            <f.icon size={14} color={f.color} />
            <span className="text-xs font-semibold" style={{ color: "rgba(245,245,245,0.85)" }}>{f.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
